import React, { createContext, useContext, useEffect } from 'react';
import { useAtom } from "jotai";
import { themeAtom } from '@app/models/AppStates';
import { ThemeEnum } from '@app/models/ThemeEnum';

type Theme = ThemeEnum;

interface ThemeContextType {
    theme: Theme;
    toggleTheme: () => void;
    setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = (): ThemeContextType => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

interface ThemeProviderProps {
    children: React.ReactNode;
}

const THEME_LINK_ID = 'primereact-theme-link';

const loadTheme = (theme: Theme) => {
    const themeLink = document.getElementById(THEME_LINK_ID) as HTMLLinkElement;
    const themePath = theme === ThemeEnum.LIGHT 
        ? '/themes/lara-light-blue/theme.css'
        : '/themes/lara-dark-blue/theme.css';
    
    if (themeLink) {
        themeLink.href = themePath;
    } else {
        // Create theme link element if it doesn't exist
        const newThemeLink = document.createElement('link');
        newThemeLink.id = THEME_LINK_ID;
        newThemeLink.rel = 'stylesheet';
        newThemeLink.href = themePath;
        document.head.appendChild(newThemeLink);
    }
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
    const [theme, setThemeState] = useAtom(themeAtom);

    const toggleTheme = () => {
        const newTheme = theme === ThemeEnum.LIGHT ? ThemeEnum.DARK : ThemeEnum.LIGHT;
        setThemeState(newTheme);
    };

    const setTheme = (newTheme: Theme) => {
        setThemeState(newTheme);
    };

    useEffect(() => {
        // Set data attribute for custom CSS overrides
        document.documentElement.setAttribute('data-theme', theme);
        
        // Load the appropriate PrimeReact theme
        loadTheme(theme);
        
        // Update body background color to match theme
        document.body.style.backgroundColor = theme === ThemeEnum.LIGHT ? '#ffffff' : '#111827';
        document.body.style.color = theme === ThemeEnum.LIGHT ? '#000000' : '#ffffff';
    }, [theme]);

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};